'use client';

import { useState } from 'react';
import { favoritesApi } from '@/lib/api';

interface SaveToFavoritesModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  productName: string;
  productNameHe: string;
  selectedOptions: Record<string, any>;
  specialInstructions?: string;
  onSuccess?: () => void;
}

export default function SaveToFavoritesModal({
  isOpen,
  onClose,
  productId,
  productName,
  productNameHe,
  selectedOptions,
  specialInstructions,
  onSuccess,
}: SaveToFavoritesModalProps) {
  const [favoriteName, setFavoriteName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!favoriteName.trim()) {
      setError('יש להזין שם למועדף');
      return;
    }

    // Check if user is logged in
    const token = localStorage.getItem('amam_token');
    if (!token) {
      setError('יש להתחבר כדי לשמור מועדפים');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await favoritesApi.createFavorite({
        name: favoriteName.trim(),
        productId,
        selectedOptions,
        specialInstructions,
      });

      setFavoriteName('');
      onSuccess?.();
      onClose();
      alert('הפריט נשמר למועדפים בהצלחה!');
    } catch (err: any) {
      console.error('Failed to save favorite:', err);
      if (err.response?.status === 401) {
        setError('יש להתחבר כדי לשמור מועדפים');
      } else {
        setError('שגיאה בשמירת המועדף. נסה שנית.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFavoriteName('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">שמור למועדפים</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
            aria-label="סגור"
          >
            ✕
          </button>
        </div>

        {/* Product Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="font-semibold text-gray-900">{productNameHe || productName}</p>
          {specialInstructions && (
            <p className="text-sm text-gray-600 mt-2 italic">"{specialInstructions}"</p>
          )}
        </div>

        {/* Favorite Name Input */}
        <div className="mb-6">
          <label htmlFor="favoriteName" className="block text-sm font-medium text-gray-700 mb-2">
            שם המועדף <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="favoriteName"
            value={favoriteName}
            onChange={(e) => {
              setFavoriteName(e.target.value);
              setError('');
            }}
            placeholder="לדוגמה: הפיצה שלי, ארוחה קבועה..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={saving}
            autoFocus
          />
          {error && (
            <p className="text-red-600 text-sm mt-2">{error}</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            disabled={saving}
          >
            ביטול
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !favoriteName.trim()}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <span className="animate-spin">⏳</span>
                שומר...
              </>
            ) : (
              <>
                ❤️ שמור
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
